import type { S3File } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	FileIcon,
} from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { privateAxios } from "../../../../../api/axios";
import { useUploadBrokerDocument } from "../../../../../api/mutations/societies.mutations";
import { societiesQueryKeys } from "../../../../../api/query-keys/societies.query-keys";
import { S3FileInput } from "../../../../../component/common/S3FileInput";

interface MarkConventionSignedModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	societyId: number;
	taskId: number;
	onSuccess?: () => void;
}

interface TaskFile {
	file: {
		id: string;
		name: string;
		key: string;
		mimeType: string;
	};
}

const MarkConventionSignedModal: React.FC<MarkConventionSignedModalProps> = ({
	open,
	onOpenChange,
	societyId,
	taskId,
	onSuccess,
}) => {
	const [uploadedFile, setUploadedFile] = useState<S3File | null>(null);
	const [isMarking, setIsMarking] = useState(false);
	const [taskFiles, setTaskFiles] = useState<TaskFile[]>([]);
	const [fileId, setFileId] = useState<string | null>(null);
	const queryClient = useQueryClient();
	const uploadDocument = useUploadBrokerDocument();

	useEffect(() => {
		if (open && taskId) {
			fetchTaskFiles();
		}
	}, [open, taskId]);

	const fetchTaskFiles = async () => {
		try {
			const response = await privateAxios.get(`/tasks/${taskId}`);
			if (response.data?.files) {
				setTaskFiles(response.data.files);
			}
		} catch (error) {
			console.error(
				"Erreur lors du chargement des fichiers de la tâche:",
				error,
			);
		}
	};

	const handleFileUpload = async (file: S3File) => {
		setUploadedFile(file);

		try {
			const uploadedDoc = await uploadDocument.mutateAsync({
				brokerId: societyId,
				name: file.name,
				key: file.key,
				bucket: file.bucket,
				mimeType: file.type,
				type: "CONVENTION",
			});

			setFileId(uploadedDoc.file.id);

			await privateAxios.post(`/tasks/${taskId}/files`, {
				fileId: uploadedDoc.file.id,
			});

			toast.success("Document ajouté à la tâche");
			await fetchTaskFiles();
		} catch (error) {
			console.error("Erreur lors de l'upload:", error);
			toast.error("Erreur lors de l'upload du document");
			setUploadedFile(null);
			setFileId(null);
		}
	};

	const handleMarkAsSigned = async () => {
		setIsMarking(true);
		try {
			await privateAxios.post(`/tasks/${taskId}/mark-signed`, {
				fileId: fileId,
			});

			toast.success("Convention marquée comme signée avec succès");

			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerTasks(societyId),
			});
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDocuments(societyId),
			});
			await queryClient.invalidateQueries({
				queryKey: ["task", taskId],
			});

			onOpenChange(false);
			onSuccess?.();
		} catch (error) {
			console.error("Erreur lors du marquage:", error);

			if (error instanceof AxiosError && error.response?.data?.message) {
				toast.error(error.response.data.message);
			} else {
				toast.error("Erreur lors du marquage de la convention");
			}
		} finally {
			setIsMarking(false);
		}
	};

	const hasFiles = taskFiles.length > 0 || uploadedFile !== null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Marquer la convention comme signée</DialogTitle>
					<DialogDescription>
						Uploadez la convention signée manuellement pour terminer cette
						tâche.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{taskFiles.length > 0 && (
						<div className="space-y-2">
							<h3 className="text-sm font-medium text-gray-900">
								Documents déjà ajoutés :
							</h3>
							<div className="space-y-1">
								{taskFiles.map((taskFile) => (
									<div
										key={taskFile.file.id}
										className="flex items-center gap-2 p-2 rounded-lg bg-gray-50"
									>
										<FileIcon
											mimeType={taskFile.file.mimeType}
											className="h-4 w-4"
										/>
										<span className="text-sm text-gray-700">
											{taskFile.file.name}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					<div>
						<div className="text-sm font-medium mb-2">Convention signée</div>
						<S3FileInput
							key={`upload-convention-${open}`}
							bucket="private"
							onUploadSuccess={handleFileUpload}
							hidePreviewButton={true}
							subText="Format accepté : PDF uniquement"
						/>
					</div>

					<div className="rounded-lg bg-blue-50 p-3">
						<p className="text-sm text-blue-800">
							Une fois la convention signée uploadée, elle remplacera
							automatiquement la version non signée dans les documents de la
							société.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isMarking}
					>
						Annuler
					</Button>
					<Button
						onClick={handleMarkAsSigned}
						disabled={!hasFiles || isMarking}
						isLoading={isMarking}
					>
						Marquer comme signée
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default MarkConventionSignedModal;
